import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Card, Row, Col, Button, Tag, Typography, Space, Badge,
  Statistic, Progress, Tooltip, Input, Select, Divider,
  Alert, Tabs, Table, Modal, Empty, Spin, notification,
} from "antd";
import {
  CheckCircleOutlined, CloseCircleOutlined, EditOutlined,
  MinusCircleOutlined, ExclamationCircleOutlined, TrophyOutlined,
  FilterOutlined, UserOutlined, SaveOutlined, BulbOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import api from "../services/api";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const VERDICT_CONFIG = {
  APPROVED:  { color: "success", icon: <CheckCircleOutlined />,   label: "Correct",   bg: "#f6ffed", border: "#b7eb8f" },
  REJECTED:  { color: "error",   icon: <CloseCircleOutlined />,   label: "Wrong",     bg: "#fff2f0", border: "#ffccc7" },
  CORRECTED: { color: "warning", icon: <EditOutlined />,          label: "Corrected", bg: "#fffbe6", border: "#ffe58f" },
  SKIP:      { color: "default", icon: <MinusCircleOutlined />,   label: "Skip",      bg: "#fafafa", border: "#d9d9d9" },
  PENDING:   { color: "default", icon: <ExclamationCircleOutlined />, label: "Pending", bg: "#fff", border: "#d9d9d9" },
};

const PRIORITY_CONFIG = {
  HIGH:   { color: "red",    label: "🚨 High Priority" },
  MEDIUM: { color: "orange", label: "⚠️ Medium" },
  LOW:    { color: "default",label: "Low" },
};

export default function HumanReviewPage() {
  const { resultId }    = useParams();
  const [params]        = useSearchParams();
  const reviewerName    = params.get("reviewer") || "Analyst";

  const [review, setReview]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [activeTab, setActiveTab] = useState("priority");
  const [filter, setFilter]       = useState("ALL");

  // Local verdict state (batch submit)
  const [localVerdicts, setLocalVerdicts] = useState({});
  const [activeField, setActiveField]     = useState(null);
  const [correctionModal, setCorrectionModal] = useState(null);

  useEffect(() => {
    loadReview();
  }, [resultId]);

  const loadReview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/validate/review/${resultId}`);
      setReview(data);
      // Initialize local verdicts from existing data
      const init = {};
      (data.allFields || []).forEach((f) => {
        if (f.verdict !== "PENDING") {
          init[f.field] = { verdict: f.verdict, correctedValue: f.correctedValue, reviewerNote: f.reviewerNote };
        }
      });
      setLocalVerdicts(init);
    } catch {
      notification.error({ message: "Could not load review task" });
    }
    setLoading(false);
  };

  const setVerdict = (field, verdict) => {
    if (verdict === "CORRECTED") {
      setCorrectionModal(field);
      return;
    }
    setLocalVerdicts((prev) => ({ ...prev, [field]: { verdict, correctedValue: null, reviewerNote: null } }));
  };

  const saveCorrectedValue = (field, correctedValue, reviewerNote) => {
    setLocalVerdicts((prev) => ({ ...prev, [field]: { verdict: "CORRECTED", correctedValue, reviewerNote } }));
    setCorrectionModal(null);
  };

  const submitBatch = async () => {
    const pendingVerdicts = Object.entries(localVerdicts)
      .filter(([, v]) => v.verdict && v.verdict !== "PENDING")
      .map(([field, v]) => ({ field, ...v }));

    if (pendingVerdicts.length === 0) {
      notification.info({ message: "No verdicts to submit yet" });
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.patch(`/validate/review/${review._id}/verdicts`, {
        verdicts: pendingVerdicts,
        reviewerName,
      });
      setReview(data);
      notification.success({ message: `✅ ${pendingVerdicts.length} verdicts saved` });
    } catch {
      notification.error({ message: "Save failed" });
    }
    setSaving(false);
  };

  const getFieldVerdict = (fieldPath) =>
    localVerdicts[fieldPath]?.verdict || "PENDING";

  const getDisplayFields = (fields) => {
    let filtered = fields || [];
    if (filter !== "ALL") filtered = filtered.filter((f) => getFieldVerdict(f.field) === filter);
    return filtered;
  };

  if (loading) return <Spin size="large" style={{ display: "block", margin: "80px auto" }} />;
  if (!review) return <Alert type="error" message="Review task not found" />;

  const priorityFields = review.priorityFields || [];
  const allFields      = review.allFields || [];
  const pendingAll     = allFields.filter((f) => getFieldVerdict(f.field) === "PENDING").length;
  const submittedCount = Object.keys(localVerdicts).length;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <SafetyCertificateOutlined style={{ fontSize: 24, color: "#1A3C5E" }} />
            <div>
              <Title level={3} style={{ margin: 0, color: "#1A3C5E" }}>Human Review</Title>
              <Text type="secondary">
                {review.modelId?.toUpperCase()} · {review.filingId?.companyName || "Filing"} ·{" "}
                <UserOutlined /> {reviewerName}
              </Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space>
            <Badge count={submittedCount} color="#1A3C5E">
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                onClick={submitBatch}
                style={{ background: "#1A3C5E" }}
              >
                Save {submittedCount} Verdict{submittedCount !== 1 ? "s" : ""}
              </Button>
            </Badge>
          </Space>
        </Col>
      </Row>

      {/* ── Progress Bar ─────────────────────────────────────────── */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={24} align="middle">
          <Col flex="auto">
            <Text strong>Review Progress</Text>
            <Progress
              percent={Math.round(((allFields.length - pendingAll) / allFields.length) * 100)}
              strokeColor={{ "0%": "#1A3C5E", "100%": "#52c41a" }}
              style={{ marginTop: 4 }}
            />
          </Col>
          <Col>
            <Statistic title="Pending"   value={pendingAll}                                   valueStyle={{ color: "#faad14" }} />
          </Col>
          <Col>
            <Statistic title="Approved"  value={Object.values(localVerdicts).filter(v => v.verdict === "APPROVED").length}  valueStyle={{ color: "#52c41a" }} />
          </Col>
          <Col>
            <Statistic title="Rejected"  value={Object.values(localVerdicts).filter(v => v.verdict === "REJECTED").length}  valueStyle={{ color: "#ff4d4f" }} />
          </Col>
          <Col>
            <Statistic title="Corrected" value={Object.values(localVerdicts).filter(v => v.verdict === "CORRECTED").length} valueStyle={{ color: "#fa8c16" }} />
          </Col>
          {review.humanValidatedScore !== null && review.humanValidatedScore !== undefined && (
            <Col>
              <Statistic
                title={<><TrophyOutlined /> Validated Score</>}
                value={review.humanValidatedScore}
                suffix="%"
                valueStyle={{ color: "#1A3C5E", fontWeight: 700, fontSize: 28 }}
              />
            </Col>
          )}
        </Row>
      </Card>

      {/* ── Status Banner ─────────────────────────────────────────── */}
      {review.status === "COMPLETE" && (
        <Alert
          type="success"
          showIcon
          message={`Review complete — Human-validated accuracy: ${review.humanValidatedScore}%`}
          style={{ marginBottom: 16 }}
        />
      )}

      {priorityFields.length > 0 && review.status !== "COMPLETE" && (
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message={`${priorityFields.length} fields flagged by automated layers — review these first`}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* ── Filter Bar ────────────────────────────────────────────── */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space>
          <FilterOutlined />
          <Text strong>Filter:</Text>
          {["ALL", "PENDING", "APPROVED", "REJECTED", "CORRECTED"].map((v) => (
            <Button
              key={v}
              size="small"
              type={filter === v ? "primary" : "default"}
              style={filter === v ? { background: "#1A3C5E" } : {}}
              onClick={() => setFilter(v)}
            >
              {v}
            </Button>
          ))}
        </Space>
      </Card>

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "priority",
            label: (
              <span>
                🚨 Flagged Fields
                <Badge count={priorityFields.length} style={{ marginLeft: 6, background: "#ff4d4f" }} />
              </span>
            ),
            children: (
              <FieldReviewList
                fields={getDisplayFields(priorityFields)}
                localVerdicts={localVerdicts}
                onVerdict={setVerdict}
                onCorrectionClick={setCorrectionModal}
                emptyText="No flagged fields — all automated checks passed!"
              />
            ),
          },
          {
            key: "all",
            label: (
              <span>
                All Fields
                <Badge count={allFields.length} style={{ marginLeft: 6, background: "#1A3C5E" }} />
              </span>
            ),
            children: (
              <FieldReviewList
                fields={getDisplayFields(allFields)}
                localVerdicts={localVerdicts}
                onVerdict={setVerdict}
                onCorrectionClick={setCorrectionModal}
                emptyText="No fields to review"
              />
            ),
          },
        ]}
      />

      {/* ── Correction Modal ─────────────────────────────────────── */}
      <CorrectionModal
        fieldPath={correctionModal}
        review={review}
        onSave={saveCorrectedValue}
        onCancel={() => setCorrectionModal(null)}
      />
    </div>
  );
}

// ── Field Review List ──────────────────────────────────────────────────────

function FieldReviewList({ fields, localVerdicts, onVerdict, onCorrectionClick, emptyText }) {
  if (fields.length === 0) {
    return <Empty description={emptyText} style={{ padding: "40px 0" }} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {fields.map((f) => (
        <FieldReviewCard
          key={f.field}
          field={f}
          verdict={localVerdicts[f.field]?.verdict || "PENDING"}
          correctedValue={localVerdicts[f.field]?.correctedValue}
          onVerdict={(v) => onVerdict(f.field, v)}
          onCorrectionClick={() => onCorrectionClick(f.field)}
        />
      ))}
    </div>
  );
}

// ── Individual Field Card ──────────────────────────────────────────────────

function FieldReviewCard({ field, verdict, correctedValue, onVerdict, onCorrectionClick }) {
  const vc = VERDICT_CONFIG[verdict] || VERDICT_CONFIG.PENDING;
  const pc = PRIORITY_CONFIG[field.priority] || PRIORITY_CONFIG.LOW;

  return (
    <Card
      size="small"
      style={{
        border: `1px solid ${vc.border}`,
        background: vc.bg,
        transition: "all 0.2s",
      }}
    >
      <Row align="middle" gutter={12}>
        {/* Field info */}
        <Col flex="1">
          <Space direction="vertical" size={2}>
            <Space>
              <Text code style={{ fontSize: 12 }}>{field.field}</Text>
              {field.priority !== "LOW" && (
                <Tag color={pc.color} style={{ fontSize: 10 }}>{pc.label}</Tag>
              )}
            </Space>

            <Row gutter={16}>
              <Col>
                <Text type="secondary" style={{ fontSize: 11 }}>AI Value: </Text>
                <Text strong style={{ fontSize: 13 }}>
                  {field.aiValue != null ? String(field.aiValue).slice(0, 60) : "—"}
                </Text>
              </Col>
              {field.xbrlValue != null && (
                <Col>
                  <Text type="secondary" style={{ fontSize: 11 }}>XBRL: </Text>
                  <Text style={{ fontSize: 13, color: "#1A3C5E" }}>
                    {field.xbrlValue.toLocaleString()}
                  </Text>
                </Col>
              )}
              {correctedValue != null && (
                <Col>
                  <Text type="secondary" style={{ fontSize: 11 }}>Corrected: </Text>
                  <Text style={{ fontSize: 13, color: "#fa8c16", fontWeight: 600 }}>{correctedValue}</Text>
                </Col>
              )}
            </Row>

            {/* Flags from other layers */}
            {field.consensusFlag && (
              <Text style={{ fontSize: 11, color: "#fa8c16" }}>⚠️ {field.consensusFlag}</Text>
            )}
            {field.formulaFlag && (
              <Text style={{ fontSize: 11, color: "#ff4d4f" }}>📐 {field.formulaFlag}</Text>
            )}
          </Space>
        </Col>

        {/* Verdict buttons */}
        <Col>
          <Space>
            <Tooltip title="Mark as correct">
              <Button
                size="small"
                type={verdict === "APPROVED" ? "primary" : "default"}
                icon={<CheckCircleOutlined />}
                style={verdict === "APPROVED" ? { background: "#52c41a", borderColor: "#52c41a" } : {}}
                onClick={() => onVerdict("APPROVED")}
              >
                Correct
              </Button>
            </Tooltip>
            <Tooltip title="Mark as wrong">
              <Button
                size="small"
                danger
                type={verdict === "REJECTED" ? "primary" : "default"}
                icon={<CloseCircleOutlined />}
                onClick={() => onVerdict("REJECTED")}
              >
                Wrong
              </Button>
            </Tooltip>
            <Tooltip title="Provide correct value">
              <Button
                size="small"
                type={verdict === "CORRECTED" ? "primary" : "default"}
                icon={<EditOutlined />}
                style={verdict === "CORRECTED" ? { background: "#fa8c16", borderColor: "#fa8c16" } : {}}
                onClick={onCorrectionClick}
              >
                Correct it
              </Button>
            </Tooltip>
            <Tooltip title="Skip this field">
              <Button
                size="small"
                type={verdict === "SKIP" ? "primary" : "default"}
                icon={<MinusCircleOutlined />}
                onClick={() => onVerdict("SKIP")}
              >
                Skip
              </Button>
            </Tooltip>
          </Space>
        </Col>
      </Row>
    </Card>
  );
}

// ── Correction Modal ───────────────────────────────────────────────────────

function CorrectionModal({ fieldPath, review, onSave, onCancel }) {
  const [value, setValue] = useState("");
  const [note, setNote]   = useState("");

  useEffect(() => { if (fieldPath) { setValue(""); setNote(""); } }, [fieldPath]);

  const field = review?.allFields?.find((f) => f.field === fieldPath);

  return (
    <Modal
      open={!!fieldPath}
      title={<><EditOutlined /> Provide Correct Value</>}
      onOk={() => onSave(fieldPath, value, note)}
      onCancel={onCancel}
      okText="Save Correction"
      okButtonProps={{ style: { background: "#1A3C5E" } }}
    >
      {field && (
        <>
          <Paragraph>
            <Text type="secondary">Field: </Text>
            <Text code>{fieldPath}</Text>
          </Paragraph>
          <Paragraph>
            <Text type="secondary">AI extracted: </Text>
            <Text strong>{field.aiValue != null ? String(field.aiValue) : "—"}</Text>
          </Paragraph>
          {field.xbrlValue != null && (
            <Paragraph>
              <Text type="secondary">XBRL value: </Text>
              <Text style={{ color: "#1A3C5E", fontWeight: 600 }}>{field.xbrlValue.toLocaleString()}</Text>
            </Paragraph>
          )}
          <Divider />
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <Text strong>Correct Value:</Text>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter the correct value"
                style={{ marginTop: 4 }}
              />
            </div>
            <div>
              <Text strong>Note (optional):</Text>
              <TextArea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. AI confused gross vs net, unit was billions not millions..."
                rows={2}
                style={{ marginTop: 4 }}
              />
            </div>
          </Space>
        </>
      )}
    </Modal>
  );
}